/* eslint-disable no-restricted-properties, no-restricted-syntax, guard-for-in, no-prototype-builtins, no-restricted-globals */
import moment from 'moment'
import _ from 'lodash'
import localStore from 'store'

export const hasRights = (membership, role) => {
  if (!membership) return false
  const projectRole = typeof membership === 'object' ? membership.projectRole : membership

  if (
    !projectRole ||
    !['SUPPORT', 'OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'LIMITED_VIEWER'].includes(projectRole)
  )
    return false
  if (!['SUPPORT', 'OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'LIMITED_VIEWER'].includes(role))
    return false

  switch (role) {
    case 'LIMITED_VIEWER':
      return ['SUPPORT', 'OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'LIMITED_VIEWER'].includes(
        projectRole,
      )
    case 'VIEWER':
      return ['SUPPORT', 'OWNER', 'ADMIN', 'EDITOR', 'VIEWER'].includes(projectRole)
    case 'EDITOR':
      return ['SUPPORT', 'OWNER', 'ADMIN', 'EDITOR'].includes(projectRole)
    case 'ADMIN':
      return ['SUPPORT', 'OWNER', 'ADMIN'].includes(projectRole)
    case 'OWNER':
      return ['SUPPORT', 'OWNER'].includes(projectRole)
    case 'SUPPORT':
      return ['SUPPORT'].includes(projectRole)
    default:
      return false
  }
}

// EDITOR is a legacy role, not supported anymore -> Legacy-compatible method for access-rights edit-check
export const allowEditRights = (targetRole, selfRole) => {
  const roles = getEditRights(selfRole)
  return roles.length > 0 ? [...roles, 'EDITOR'].includes(targetRole) : false
}

export const getEditRights = (projectRole) => {
  if (hasRights(projectRole, 'OWNER')) return ['LIMITED_VIEWER', 'VIEWER', 'ADMIN', 'OWNER']
  if (hasRights(projectRole, 'ADMIN')) return ['LIMITED_VIEWER', 'VIEWER']
  return []
}

export const isNumber = (n) => !Number.isNaN(Number(n))
export const isObject = (v) => typeof v === 'object' && v !== null
export const isEmptyObject = (object) =>
  isEmpty(object) || (object instanceof Object && Object.keys(object).length === 0)
export const isEmptyNestedObject = (obj) => isEmptyObject(obj) || !flatten(obj).some((n) => !!n)

export const SafeAddition = (a, b) => Math.round((a + b) * 1e12) / 1e12
export const SafeSubtraction = (a, b) => Math.round((a - b) * 1e12) / 1e12
export const CountDecimals = (value) => {
  if (Math.floor(value) === value) return 0
  return value.toString().split('.')[1].length || 0
}
export function TextSort(a, b) {
  if (a < b) return 1
  if (a > b) return -1
  return 0
}

// Get the order of magnitude of a number e.g. 53232 => 10000, useful to get a good decimal rounding if we know the maximum division
export const getNumberMagnitude = (n) => {
  const order = Math.floor(Math.log(Math.abs(n)) / Math.LN10 + 0.000000001) // because float math sucks like that
  return Math.pow(10, order)
}

// Percentage precision between 2 and 4 digits depending on number length
export const getPercentagePrecision = (integer) =>
  Math.max(2, Math.min(integer.toString().length - 1, 4))

// Get path from object, either "aa.bb" or ["aa", "bb"]
// returns the resolved value
export const get = (path, object, fallback = null) => {
  return _.get(object, path, fallback)
}

// Set path from object, either "aa.bb" or ["aa", "bb"]
// returns the object
export const set = (path, object, value) => {
  return _.set(object, path, value)
}

// searchObject(obj, 'hello.is.there.anything.here')
export const searchObject = (object, query, fallback) => get(query.split('.'), object, fallback)

// Flats an object into Array
export const flatten = (obj) => Object.values(flattenObj(obj))

/**
 * Flatten a multidimensional object
 *
 * For example:
 *   flattenObject({ a: 1, b: { c: 2 } })
 * Returns:
 *   { a: 1, c: 2}
 */
export const flattenObj = (obj) => {
  if (!isObject(obj)) throw Error('Flatten method only accepts objects!')
  const flattened = {}

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(flattened, flatten(obj[key]))
    } else {
      flattened[key] = obj[key]
    }
  })

  return flattened
}

/**
 * use this to allow null when also required (watch Popup/index.js)
 * @param {PropTypes} wrappedPropTypes
 */
export const allowNull = (wrappedPropTypes) => {
  return (props, propName, ...rest) => {
    if (props[propName] === null) return null
    return wrappedPropTypes(props, propName, ...rest)
  }
}

/**
 * this will iterate the object and execute the callback on each key
 * @param {Object} object
 * @param {Function} callback (object, string)
 */
export const objectMap = (object, callback) => {
  if (isEmptyObject(object)) return
  Object.keys(object).forEach((key) => {
    callback(object[key], key)
  })
}

window.moment = moment // FOR debugging

// Apollo 3.0 Client is using the nonExtensible flag for objects.. not sure how to disable
export const unfreeze = (object) => JSON.parse(JSON.stringify(object))

export const deepFreeze = (object) => {
  Object.keys(object).forEach((property) => {
    if (typeof object[property] === 'object' && !Object.isFrozen(object[property]))
      deepFreeze(object[property])
  })
  return Object.freeze(object)
}

export const copyNested = (object) => unfreeze(object)

/**
 * Function to use when assigning something by unique name (e.g. C3Charts where name is also used as key)
 * @param assign - the object/array to check for existing name keys
 * @param name - the key, in this case name (thus can be duplicate)
 * @param idx - an increasing index to distinguish duplicates
 */
export function findUniqueNameKey(assign, name, idx = 2) {
  return (assign instanceof Array ? assign.some((o) => o.name === name) : !!assign[name])
    ? findUniqueNameKey(assign, `${name} (${idx})`, ++idx)
    : name
}

// ES6 groupBy for javascript arrays
export const groupBy = (list, keyGetter) => {
  const map = new Map()
  list.forEach((item) => {
    const key = keyGetter(item)
    const collection = map.get(key)
    if (!collection) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })
  return map
}

// Already existed similar function but with object instead of Map return type
export function groupByKeys(xs, ...keys) {
  return xs.reduce((rv, x) => {
    ;(rv[multiKey(x, ...keys)] = rv[multiKey(x, ...keys)] || []).push(x)
    return rv
  }, {})
}

export function multiKey(object, ...keys) {
  return keys.reduce((r, o) => r[o], object)
}

export const sumByKey = (array, keyFunc) => {
  return array.reduce((total, item) => total + keyFunc(item), 0)
}

export const sameObject = (a, b, ignoreMoment = true) => {
  if (a instanceof Array) return sameArrays(a, b, ignoreMoment)

  // moment.isSame is good, but the data in one case was not..
  // ...it was filled everytime it got focues.. added back ignore
  if (a instanceof moment && b instanceof moment) return ignoreMoment || a.isSame(b)

  if (a instanceof Object) {
    if (b instanceof Array) return false
    if (b instanceof Object) {
      if (Object.keys(a).length !== Object.keys(b).length) return false

      for (const key in a) {
        if (!b.hasOwnProperty(key)) return false
        if (!sameObject(a[key], b[key], ignoreMoment)) return false
      }
    } else return false
  } else if (a !== b && !(isEmpty(a) && isEmpty(b))) return false

  return true
}

export const sameArrays = (a, b, ignoreMoment) => {
  if (b instanceof Array) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!sameObject(a[i], b[i], ignoreMoment)) return false
    }
  } else return false

  return true
}

// Array to object with custom key/value functions
export const arrToObj = (arr = [], keyFunc = (s) => s.id, valueFunc = (s) => s) => {
  return arr.reduce(
    (o, b, idx) => ({
      ...o,
      [keyFunc(b, idx, o)]: valueFunc(b, idx, o),
    }),
    {},
  )
}

// Javascript Map to object
export const mapToObj = (map) => {
  return Array.from(map.keys()).reduce(
    (o, b) => ({
      ...o,
      [b]: map.get(b),
    }),
    {},
  )
}

// Javascript convert antD fields to values (object)
// E.g. fields = [{ name: ["test", "nestedProp"], value:"v"}]
// Will be { test: { nestedProp: "v" } }
export const fieldsToObject = (fields) => {
  return fields.reduce((o, f) => {
    _.set(o, f.name.join('.'), f.value)
    return o
  }, {})
}

// Like python range function
export const range = (size) => [...new Array(size).keys()]

export function onlyUniqueId(entity, index, self) {
  return self.findIndex((e) => entity.id === e.id) === index
}

// More general onlyUnique filter option. E.g. arr.filter(onlyUnique('name'))
export function onlyUniqueKey(keyFunc = (e) => e.id) {
  return (entity, index, self) => {
    return self.findIndex((e) => keyFunc(entity) === keyFunc(e)) === index
  }
}

// Filter only unique items in the array
export const unique = (arr) => Array.from(new Set(arr))

export function lerp(start, end, t) {
  return start * (1 - t) + end * t
}

export const equalsUntyped = (p1, p2) => {
  if (typeof p1 !== typeof p2) return false
  if (typeof p1 === 'number') return equalsDouble(p1, p2)
  return p1 === p2
}

export const equalsDouble = (v1, v2, isPercent = false) => compareDouble(v1, v2, isPercent) === 0
export const compareDouble = (v1, v2, isPercent = false) => {
  // For percent: 2 decimals precision (0.0001 => 0.01%, precision level, 5th digit is ignored)
  const d = isPercent ? 10000 : 100
  return Math.round(parseFloat(v1) * d) / d - Math.round(parseFloat(v2) * d) / d
}

export const isUUID = (uuid) => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid)
}

export const hasRemoteId = (entity) => entity && isUUID(entity.id)

// used to check if empty but allow zero -> backwars compatible version, prefer isEmptyString/isEmptyNumber
export const isEmpty = (n) => !n && isNaN(parseFloat(n))

export const isEmptyString = (string) => !string || string === ''
export const isEmptyNumber = (number) => (!number && number !== 0) || isNaN(parseFloat(number))

export function nonNull(property) {
  return property !== null && property !== undefined
}

export const objectContainsAny = (object, queries) => {
  if (isEmptyObject(object)) return false
  for (const query of queries) {
    if (!isEmptyObject(searchObject(object, query))) return true
  }

  return false
}

export const formInitialized = (form) => !isEmptyObject(form.__INTERNAL__)

export function openInNewTab(url) {
  const win = window.open(url, '_blank')
  win.focus()
}

/**
 * Shuffles array in place. ES6 version
 * @param {Array} a items An array containing the items.
 */
export function shuffle(a) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export function arrayMin(arr) {
  return arr.reduce((p, v) => {
    return p < v ? p : v
  })
}

export function arrayMax(arr) {
  return arr.reduce((p, v) => {
    return p > v ? p : v
  })
}

// Distribute the rest of a total amount (e.g. total backing needs to be fully distributed by quotas)
export function distributeRestShares(receivers, total, valuePath, filter = (x) => x) {
  const totalValues = receivers.reduce((t, r) => t + get(valuePath, r, 0), 0)
  const restAmount = [total - totalValues]
  const signedInt = restAmount[0] > 0 ? +1 : -1

  if (restAmount[0] === 0) return receivers

  return receivers.map((r) => {
    if (filter(r) || restAmount[0] === 0) return r
    restAmount[0] -= signedInt
    set(valuePath, r, get(valuePath, r, 0) + signedInt)
    return r
  })
}

export const getLocale = () => {
  const locale = localStore.get('app.settings.locale')
  return locale
}

/* eslint-enable */
